import {
    deleteTaskImage,
    getBatchTaskImageUrls,
    getTaskImages,
    hardDeleteTaskImage,
    restoreTaskImage,
    uploadTaskImage,
} from "@dashboard/api/task";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";

export const taskImageKeys = {
    all: ["taskImages"] as const,
    lists: () => [...taskImageKeys.all, "list"] as const,
    listsByTaskId: (taskId: number) => [...taskImageKeys.lists(), taskId] as const,
    urls: () => [...taskImageKeys.all, "urls"] as const,
    batchUrls: (imageIds: number[]) =>
        [...taskImageKeys.urls(), [...imageIds].sort().join(",")] as const,
};

export const useTaskImages = (taskId: number) => {
    const queryClient = useQueryClient();

    const query = useQuery({
        queryKey: taskImageKeys.listsByTaskId(taskId),
        queryFn: () => getTaskImages(taskId),
        enabled: !!taskId,
    });

    useEffect(() => {
        if (query.data?.data && query.data.data.length > 0) {
            const imageIds = query.data.data.map((img) => img.id);
            queryClient.prefetchQuery({
                queryKey: taskImageKeys.batchUrls(imageIds),
                queryFn: () => getBatchTaskImageUrls(imageIds),
                staleTime: (query) => {
                    const data = query.state.data;
                    if (!data || data.length === 0) return 0;
                    const earliestExpiry = Math.min(...data.map((item) => item.expiresAt));
                    const timeUntilExpiry = earliestExpiry - Date.now();
                    return Math.max(timeUntilExpiry - 5 * 60 * 1000, 0);
                },
            });
        }
    }, [query.data, queryClient]);

    return query;
};

export const useBatchTaskImageUrls = (imageIds: number[]) => {
    return useQuery({
        queryKey: taskImageKeys.batchUrls(imageIds),
        queryFn: () => getBatchTaskImageUrls(imageIds),
        enabled: imageIds.length > 0,
        staleTime: (query) => {
            const data = query.state.data;
            if (!data || data.length === 0) return 0;
            const earliestExpiry = Math.min(...data.map((item) => item.expiresAt));
            const timeUntilExpiry = earliestExpiry - Date.now();
            return Math.max(timeUntilExpiry - 5 * 60 * 1000, 0);
        },
    });
};

export const useUploadTaskImage = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ taskId, file }: { taskId: number; file: File }) =>
            uploadTaskImage(taskId, file),
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({
                queryKey: taskImageKeys.listsByTaskId(variables.taskId),
            });
        },
    });
};

export const useDeleteTaskImage = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ imageId }: { imageId: number; taskId?: number }) => deleteTaskImage(imageId),
        onSuccess: (_, variables) => {
            if (variables.taskId) {
                queryClient.invalidateQueries({
                    queryKey: taskImageKeys.listsByTaskId(variables.taskId),
                });
            }
        },
    });
};

export const useRestoreTaskImage = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ imageId }: { imageId: number; taskId: number }) => restoreTaskImage(imageId),
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({
                queryKey: taskImageKeys.listsByTaskId(variables.taskId),
            });
        },
    });
};

export const useHardDeleteTaskImage = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ imageId }: { imageId: number; taskId: number }) =>
            hardDeleteTaskImage(imageId),
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({
                queryKey: taskImageKeys.listsByTaskId(variables.taskId),
            });
        },
    });
};
